import i18n from "@/locales";
import { useToast } from "@/composables/useToast.js";

// Codes meaning the user deliberately backed out. These produce no toast at
// all: the user performed the action on purpose and already knows the outcome.
// Showing a red error for a decision they made reads as a bug.
const SILENT_AUTH_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

// Native cancellation codes that arrive WITHOUT the "auth/" prefix, forwarded
// straight from the platform SDK by the Capacitor FirebaseAuthentication
// plugin (it only rewrites FirebaseAuthException into an "auth/..." code, so a
// platform-level cancel passes through raw):
//
//   1001  - Apple ASAuthorizationError.canceled: the iOS Sign in with Apple
//           sheet was dismissed.
//   12501 - Google Play Services SIGN_IN_CANCELLED: the Android account
//           chooser or consent screen was backed out of. This is the single
//           most common cancellation on Android, so omitting it would show a
//           red error toast for the most routine "never mind" there is.
//
// Compared as strings so a numeric code from the native layer and a string
// from the JS bridge both match. Safe to key on bare numbers because this is
// only consulted in the auth domain, where Firebase's own codes are all
// "auth/"-prefixed and therefore cannot collide.
//
// Deliberately NOT included: Google's 12500 (SIGN_IN_FAILED) and Play
// Services' generic CommonStatusCodes.CANCELED (16), which is raised for
// non-user causes such as the call being cancelled by the system.
const SILENT_NATIVE_CODES = new Set(["1001", "12501"]);

// A message consisting of nothing but a native status code, with or without
// Android's trailing "<code>: " separator. See isUserCancellation for why the
// separator form is the one that actually shows up in the wild.
const BARE_NATIVE_CODE_MESSAGE = /^(\d+)\s*:?\s*$/;

// Apple's cancel signature when the plugin carries no usable `code` and buries
// the reason in the message.
//
// One alternative names the USER as the actor; the other matches Apple's exact
// NSError description format, which reads:
//   "The operation couldn't be completed.
//    (com.apple.AuthenticationServices.AuthorizationError error 1001.)"
//
// Four looser forms were tried and rejected during review, each of which
// swallowed a real failure:
//   - the bare word "cancelled" anywhere         -> ate "the request was cancelled"
//   - a bare \b1001\b                            -> ate "Connection timeout on port 1001"
//   - "cancelled the authorization" (no subject) -> ate "The server cancelled the authorization"
//   - "authorizationerror" within 40 chars of 1001 -> ate "AuthorizationError:
//     network timeout at auth.js line 1001" (a proximity match will always be
//     defeated by a line number, byte count or PID landing near the name)
//
// Silently dropping a real failure is the worst outcome this module can produce,
// so the bar for going silent is deliberately high. Widen it only with a test.
const APPLE_CANCEL_MESSAGE =
  /\bauthorizationerror\s+error\s+1001\b|\buser cancell?ed\b/i;

// Both lookup tables are Maps, not plain objects, so an error code or domain
// that happens to name an inherited property cannot resolve to something
// truthy. `AUTH_KEYS["constructor"]` on an object literal returns the Object
// constructor, which would sail past the `??` fallback below and hand i18n a
// function where a key string is required.
//
// auth/popup-blocked is deliberately absent from the silent set: it looks like
// a cancel, but the user did nothing and the condition is actionable.
const AUTH_KEYS = new Map([
  ["auth/network-request-failed", "errors.auth.network"],
  ["auth/popup-blocked", "errors.auth.popupBlocked"],
  ["auth/account-exists-with-different-credential", "errors.auth.accountExists"],
  ["auth/too-many-requests", "errors.auth.tooManyRequests"],
  ["auth/user-disabled", "errors.auth.userDisabled"],
]);

const DOMAIN_GENERIC = new Map([
  ["auth", "errors.auth.generic"],
  ["purchase", "errors.purchase.generic"],
]);

const AUTH_GENERIC = DOMAIN_GENERIC.get("auth");

// Both of the operations below can throw on a sufficiently hostile value:
// reading a property runs a Proxy trap or a getter, and String() throws on a
// null-prototype object, on a Symbol nested in an array, and on a
// Symbol.toPrimitive that rejects. resolveError is called from inside catch
// blocks, so a throw here would convert a handled failure into an unhandled
// one. Every read-and-coerce in this module is funnelled through this helper,
// which degrades to "" and lets the caller fall through to the generic key.
const safeString = (read) => {
  try {
    const value = read();
    if (value === null || value === undefined) return "";
    return String(value);
  } catch {
    return "";
  }
};

// Firebase puts the code on error.code. The Capacitor plugin may put it there
// or omit it entirely. Normalize whatever we get to a trimmed string.
//
// A bare string or number rejection is treated as a MESSAGE, never as a code.
// Returning it as a code would make it truthy, which short-circuits the message
// check below and would let a plain-string cancellation leak through as a
// generic error.
const extractCode = (error) => {
  if (error === null || error === undefined) return "";
  if (typeof error !== "object") return "";
  return safeString(() => error.code).trim();
};

// Mirror of extractCode: an object contributes its .message, anything else IS
// the message.
const extractMessage = (error) => {
  if (error === null || error === undefined) return "";
  if (typeof error !== "object") return safeString(() => error);
  return safeString(() => error.message);
};

const isUserCancellation = (error, code) => {
  if (SILENT_AUTH_CODES.has(code)) return true;
  if (SILENT_NATIVE_CODES.has(code)) return true;
  // The Apple sheet sometimes reports the cancel only in the message text with
  // no usable code. Two guards keep this from swallowing real failures:
  // consult the message ONLY when no code was supplied, and require an explicit
  // user-cancellation signature rather than the mere word "cancelled".
  if (code) return false;
  const message = extractMessage(error).trim();
  // The plugin can reject with the bare native status code and no surrounding
  // English text, which extractCode deliberately reads as a message rather
  // than a code. Android's ApiException builds its message as
  // `statusCode + ": " + statusMessage`, and statusMessage is null for a plain
  // cancellation, so dismissing the account chooser arrives as "12501: ".
  //
  // Anchored at both ends, so this only fires when the message is NOTHING but
  // a status code. A real failure that merely contains the number ("Connection
  // timeout on port 1001") or carries actual failure text ("12501: something
  // else entirely") still reaches the user.
  const bareNativeCode = BARE_NATIVE_CODE_MESSAGE.exec(message);
  if (bareNativeCode && SILENT_NATIVE_CODES.has(bareNativeCode[1])) return true;
  return APPLE_CANCEL_MESSAGE.test(message);
};

/**
 * Map an opaque third-party error to a friendly i18n key.
 * Pure. Never throws: it is called from inside catch blocks.
 * @param {unknown} error
 * @param {"auth"|"purchase"} domain
 * @returns {{silent: true} | {silent: false, key: string}}
 */
export const resolveError = (error, domain) => {
  const code = extractCode(error);
  if (domain === "auth" && isUserCancellation(error, code)) {
    return { silent: true };
  }
  const mapped = domain === "auth" ? AUTH_KEYS.get(code) : undefined;
  // Unknown domain degrades to the auth generic rather than throwing. Correct
  // call sites never reach this; it exists so a future typo cannot turn a
  // handled failure into an unhandled one.
  const key = mapped ?? DOMAIN_GENERIC.get(domain) ?? AUTH_GENERIC;
  return { silent: false, key };
};

/**
 * Resolve, translate, and toast an error. Synchronous by design: an async
 * variant would need vi.waitFor draining in every test that touches an error
 * path. Shows nothing when the user cancelled.
 * @param {unknown} error
 * @param {"auth"|"purchase"} domain
 */
export const showErrorToast = (error, domain) => {
  const resolved = resolveError(error, domain);
  if (resolved.silent) return;
  const { showToast } = useToast();
  showToast(i18n.global.t(resolved.key), "error");
};
